import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "radial-gradient(ellipse at center, #1a3a5c 0%, #0B1F3A 50%, #061020 100%)" }}>
      <Card className="w-full max-w-md border-border shadow-2xl" data-testid="forgot-password-page">
        <CardHeader className="space-y-1 text-center pb-2">
          <div className="flex justify-center mb-6">
            <span className="text-3xl font-black tracking-tight bg-gradient-to-r from-[#0B1F3A] to-[#3A6D8C] bg-clip-text text-transparent dark:from-white dark:to-slate-300">
              Ellalink
            </span>
          </div>
          <CardTitle className="text-2xl font-bold">{t("auth.forgotPassword")}</CardTitle>
          <CardDescription className="text-base">{t("auth.forgotPasswordDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold">{t("auth.resetEmailSent")}</h3>
              <p className="text-sm text-muted-foreground">{t("auth.resetEmailSentDesc")}</p>
              <Link to="/login">
                <Button variant="outline" className="mt-4 h-11 rounded-xl" data-testid="back-to-login-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t("auth.backToLogin")}
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">{t("auth.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input id="email" type="email" placeholder="you@example.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} className="h-12 pl-12 rounded-xl text-base" required data-testid="forgot-email-input" />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 text-base font-semibold rounded-xl shadow-lg shadow-primary/30"
                disabled={loading} data-testid="forgot-submit-btn">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t("auth.sending")}
                  </span>
                ) : (
                  <>{t("auth.sendResetLink")}<ArrowRight className="ml-2 h-5 w-5" /></>
                )}
              </Button>
              <div className="text-center text-sm text-muted-foreground pt-2">
                <Link to="/login" className="text-primary hover:text-primary/80 font-semibold transition-colors" data-testid="back-to-login-link">
                  <ArrowLeft className="inline h-4 w-4 mr-1" />
                  {t("auth.backToLogin")}
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
